import { useCallback, useEffect, useRef, useState } from "react";
import { PackageCheck, FileCode2, User, Search, ArrowLeft, Trash2, Eraser } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDT } from "@/lib/sound";
import { ConferenciaCaixas } from "@/components/ConferenciaCaixas";

const inputCls = "w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-2.5 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]";

export default function ConferenciaMercadoria() {
  const [lista, setLista] = useState([]);
  const [rec, setRec] = useState(null);
  const [conferente, setConferente] = useState(() => localStorage.getItem("conferente") || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadLista = useCallback(async () => { try { setLista((await api.get("/recebimento")).data); } catch { /* ignore */ } }, []);
  const loadRec = useCallback(async (id) => { try { setRec((await api.get(`/recebimento/${id}`)).data); } catch { toast.error("Erro ao carregar NF-e"); } }, []);
  useEffect(() => { loadLista(); }, [loadLista]);

  const apagarNF = async (r, e) => {
    e?.stopPropagation();
    if (!window.confirm(`APAGAR a NF-e ${r.nf_numero}?\n\nIsso remove definitivamente a nota e toda a conferência associada.`)) return;
    try { await api.delete(`/recebimento/${r.id}`); toast.success(`NF-e ${r.nf_numero} apagada`); loadLista(); if (rec?.id === r.id) setRec(null); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao apagar NF-e"); }
  };
  const limparNF = async (r, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Limpar a conferência de mercadoria da NF-e ${r.nf_numero}?\n\nAs caixas e os itens desta conferência serão removidos. A conferência de volumes será preservada.`)) return;
    try { const { data } = await api.post(`/recebimento/${r.id}/conferencia/limpar`); toast.success(`Conferência de mercadoria da NF-e ${r.nf_numero} limpa`); loadLista(); if (rec?.id === r.id) setRec(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao limpar conferência"); }
  };

  const startFor = (fn) => {
    if (!conferente.trim()) { toast.error("Informe o nome do conferente antes de iniciar"); return false; }
    localStorage.setItem("conferente", conferente.trim());
    fn();
    return true;
  };

  const importar = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!conferente.trim()) { toast.error("Informe o nome do conferente antes de importar"); return; }
    localStorage.setItem("conferente", conferente.trim());
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/recebimento/import", fd); setRec(data); loadLista(); toast.success(`NF-e ${data.nf_numero} importada · ${data.kpi.skus} SKUs`); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao importar XML"); } finally { setBusy(false); }
  };

  if (rec) {
    return (
      <div className="space-y-4" data-testid="conferencia-page">
        <button data-testid="conf-voltar" onClick={() => setRec(null)} className="inline-flex items-center gap-1 text-sm font-semibold text-[#0F6B3A] hover:underline"><ArrowLeft className="w-4 h-4" /> Voltar / trocar NF</button>
        <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4" data-testid="conf-resumo-nf">
          <div className="flex items-center gap-2 text-[#0F2A1F] font-bold"><PackageCheck className="w-5 h-5 text-[#0F6B3A]" /> Conferência de Mercadoria</div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
            {[["NF-e", rec.nf_numero], ["Fornecedor", rec.fornecedor?.nome || "—"], ["Emissão", rec.emissao ? String(rec.emissao).slice(0, 10) : "—"], ["Conferente", conferente || "—"], ["SKUs", rec.kpi?.skus], ["Qtd. prevista", rec.kpi?.unidades]].map(([l, v]) => (
              <div key={l}><div className="text-[10px] uppercase text-[#8A9A90]">{l}</div><div className="font-semibold text-[#0F2A1F] truncate" title={String(v)}>{v}</div></div>
            ))}
          </div>
        </div>
        <ConferenciaCaixas rec={rec} onChanged={(a) => a ? setRec(a) : loadRec(rec.id)} onLimpar={() => limparNF(rec)} onApagar={() => { apagarNF(rec); }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="conferencia-page">
      <div>
        <div className="text-[11px] font-mono tracking-[0.2em] text-[#5A6B62] uppercase">Recebimento</div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F] flex items-center gap-2"><PackageCheck className="w-7 h-7 text-[#0F6B3A]" /> Conferência de Mercadoria</h1>
        <p className="text-sm text-[#5A6B62] mt-1">Confira fisicamente a NF, bipe caixas e produtos e use o mesmo Endereçamento Inteligente do Recebimento.</p>
      </div>

      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4 space-y-3" data-testid="conf-nova">
        <div className="font-bold text-[#0F2A1F]">Nova Conferência</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <label className="text-xs font-semibold text-[#5A6B62]">Conferente</label>
            <div className="relative mt-1"><User className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" /><input data-testid="conf-conferente-input" value={conferente} onChange={(e) => setConferente(e.target.value)} className={`${inputCls} pl-9`} placeholder="Nome do conferente" /></div>
          </div>
          <div className="md:col-span-2 flex items-end">
            <input ref={fileRef} type="file" accept=".xml" className="hidden" data-testid="conf-import-input" onChange={importar} />
            <button data-testid="conf-import-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="h-11 px-4 bg-[#2DC26B] hover:bg-[#22A85B] disabled:opacity-60 text-white font-bold text-sm uppercase tracking-wide rounded-md flex items-center gap-2"><FileCode2 className="w-4 h-4" /> Importar XML da NF-e</button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4" data-testid="conf-nfs">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0F2A1F] mb-2"><Search className="w-4 h-4" /> NF-e já importadas</div>
        {lista.length === 0 ? <div className="py-8 text-center text-[#8A9A90] text-sm">Nenhuma NF-e importada ainda. Importe o XML acima.</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {lista.map((r) => (
              <div key={r.id} data-testid={`conf-nf-${r.nf_numero}`} className="border border-[#D9E3DC] rounded-lg overflow-hidden hover:border-[#2DC26B]">
                <button onClick={() => startFor(() => loadRec(r.id))} className="w-full text-left px-3 py-2 hover:bg-[#F4F8F5]">
                  <div className="font-mono font-bold text-[#0F2A1F] text-sm">NF-e {r.nf_numero}</div>
                  <div className="text-[11px] text-[#5A6B62] truncate">{r.fornecedor?.nome}</div>
                  <div className="text-[10px] text-[#8A9A90]">{r.created_at ? fmtDT(r.created_at) : ""}</div>
                </button>
                <div className="flex border-t border-[#F1F5F2] bg-[#FBFCFB]">
                  <button data-testid={`conf-nf-${r.nf_numero}-limpar`} onClick={(e) => limparNF(r, e)} className="flex-1 py-1.5 text-[11px] font-semibold text-[#9A6B0A] hover:bg-[#FFFDF4] flex items-center justify-center gap-1"><Eraser className="w-3 h-3" /> Limpar</button>
                  <div className="w-px bg-[#F1F5F2]" />
                  <button data-testid={`conf-nf-${r.nf_numero}-apagar`} onClick={(e) => apagarNF(r, e)} className="flex-1 py-1.5 text-[11px] font-semibold text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center justify-center gap-1"><Trash2 className="w-3 h-3" /> Apagar</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
